import { Stack, Card, Text, Title } from '@repletoryreach/sdk-ui';
import { Link } from 'react-router-dom';
import type { admin } from '@repletoryreach/sdk-api';
import { useLedger } from '../../../../data/useLedger';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { Indexed } from '../../../../lib/types';
import { formatDateTime, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { WalletCard } from './WalletCard';
import { InvoicePlanCard } from './InvoicePlanCard';
import { ExtraUseCard } from './ExtraUseCard';

type Entry = NonNullable<Awaited<ReturnType<typeof admin.listLedger>>['data']>[number];

export function BillingTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const t = useT();
  const { data, isLoading } = useLedger({ org_id: tenantId, limit: 50 });
  const rows = data?.data ?? [];

  return (
    <Stack gap="lg">
      <WalletCard tenantId={tenantId} tenantName={tenantName} />
      {/* The plan of a business that pays by invoice (18 October 2026). */}
      <InvoicePlanCard tenantId={tenantId} tenantName={tenantName} />
      {/* Extra use on Growth and Scale (30 October 2026), with a block for this business. */}
      <ExtraUseCard tenantId={tenantId} tenantName={tenantName} />
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('tenants.view.billing.title')}</Title>
          <DataTable<Indexed<Entry>>
            records={rows as Indexed<Entry>[]}
            loading={isLoading}
            idAccessor="id"
            emptyState={t('tenants.view.billing.empty')}
            columns={[
              { accessor: 'occurred_at', title: t('tenants.view.billing.columns.when'), render: (r) => formatDateTime(r.occurred_at) },
              {
                accessor: 'sku_code', title: t('tenants.view.billing.columns.sku'),
                // A row with no SKU says what it is: a top-up, free credit, a correction, a plan or a refund.
                render: (r) => r.sku_code ? <code>{r.sku_code}</code> : <span>{t(`tenants.view.billing.kind.${r.entry_type}`, t('tenants.view.billing.adjustment'))}</span>
              },
              {
                accessor: 'amount_minor',
                title: t('common.amount'),
                textAlign: 'right',
                // Amounts are stored in minor units. Printing the integer beside
                // the currency turned a R12.50 charge into "1250 ZAR" here while
                // the Overview card formatted the same rows correctly.
                render: (r) => formatMinor(r.amount_minor, r.currency)
              },
              { accessor: 'description', title: t('tenants.view.billing.columns.note'), render: (r) => r.description ?? t('common.notAvailable') }
            ]}
          />
          <Text fz="xs">
            <Link to={`/billing/ledger?org_id=${tenantId}`} style={{ color: 'var(--rr-color-primary)' }}>
              {t('tenants.view.billing.openLedger')}
            </Link>
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
